import QueryString from "qs";
import axios, { ServerResponse } from "../axios";
import { BASE_URL } from "@src/utils";
import { SearchedPartnerInfo, TSlotPriceDetail } from "@src/types/weddingHall";
import {
  HALL_FILTER_DEFAULT_MAX_GUARANTEE,
  HALL_FILTER_DEFAULT_MIN_GUARANTEE,
} from "@src/utils/weddingHall";
import { TContractSlotItem } from "@src/types/realtimeBooking";
const version = "20240729";

type TResult = {
  errorCode: null | string;
  errorMessage: null | string;
  result: string;
};

//인기웨딩홀 순위 조회
export const getPopularHallList = async () => {
  try {
    const res = await axios.get<any>(`${BASE_URL}/v3/book/popular-wedding-halls`);
    return res.data?.item ? res.data?.item : [];
  } catch (err) {
    return {};
  }
};

// 웨딩홀 홈(예약하기) > 인앱 메시지 조회
export const getInAppMessage = async () => {
  try {
    // 웨딩홀 예약 신청 유도 HALL_RESERVATION_APPLICATION_INDUCTION
    // 웨딩홀 추천 요청 신청 유도 HALL_RECOMMENDATION_APPLICATION_INDUCTION
    const res = await axios.get<{
      item: { name: string }[];
    }>(`${BASE_URL}/v4/page-guides?pageName=WEDDINGHALL_RESERVATION_TAB`);
    return res.data;
  } catch (err) {
    return null;
  }
};

// 웨딩홀 홈 > 웨딩홀 조회
export const getWeddingHallList = async (userType, params, fetchStart = 0) => {
  try {
    if (userType === "COMPLETE_AUTH_USER") {
      const validFilters = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => {
          if (value === 0) return true;
          return !!value !== false;
        }),
      ); // 값이 유효한 key만 필터링해서 쿼리스트링으로 넘김
      const qs = QueryString.stringify(validFilters) ? QueryString.stringify(validFilters) : "";
      const res = await axios.get<any>(
        `${BASE_URL}/v3/book/20240729/partner/halls?${qs}&fetchStart=${fetchStart}`,
      );
      return res.data;
    } else {
      return {
        data: {
          item: [],
        },
      };
    }
  } catch (err) {
    return {};
  }
};

// 웨딩홀 홈 > 웨딩홀의 시간대 더보기 조회
export const getWeddingHallMoreTimeList = async (partnerUuid, params) => {
  try {
    const validFilters = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => {
        if (value === 0) return true;
        return !!value !== false;
      }),
    ); // 값이 유효한 key만 필터링해서 쿼리스트링으로 넘김
    const qs = QueryString.stringify(validFilters) ? "?" + QueryString.stringify(validFilters) : "";
    const res = await axios.get<any>(
      `${BASE_URL}/v3/book/20240729/partner/halls/${partnerUuid}/days${qs}`,
      params,
    );
    return res.data.item;
  } catch (err) {
    return [];
  }
};

// 웨딩홀 홈 > 홀 좋아요 추가
export const addLikeWeddinghall = async (partnerUuid, partnerName) => {
  try {
    const formData = new FormData();
    formData.append("partnerUuid", partnerUuid);
    formData.append("partnerName", partnerName);
    formData.append("state", "1");
    const res = await axios.post(`${BASE_URL}/v3/like/partner`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }); // 준호님이 만드신 API
    return res.status;
  } catch (err) {
    return null;
  }
};

// 웨딩홀 홈 > 홀 좋아요 삭제
export const removeLikeWeddinghall = async (partnerUuid, partnerName) => {
  try {
    const res = await axios.delete(
      `${BASE_URL}/v3/like/partner?partnerUuid=${partnerUuid}&partnerName=${partnerName}&state=0`,
    ); // 준호님이 만드신 API
    return res.status;
  } catch (err) {
    return null;
  }
};

// 이벤트 태그 목록 조회
export const getEventTags = async (): Promise<
  { eventTagCd: string; eventTagName: string; eventTagAlias: string; tagOrd: number }[]
> => {
  try {
    const res = await axios.get<{
      item: { eventTagCd: string; eventTagName: string; eventTagAlias: string; tagOrd: number }[];
    }>(`${BASE_URL}/api/event-tags`);
    return res.data.item ?? [];
  } catch (err) {
    return [];
  }
};

// 지하철 노선 목록 조회
export const getSubwayLines = async (): Promise<
  { lineName: string; displayName: string }[]
> => {
  try {
    const res = await axios.get<{
      item: { lineName: string; displayName: string }[];
    }>(`${BASE_URL}/v4/subway/lines?type=WEDDING_HALL_FILTER`);
    return res.data.item ?? [];
  } catch (err) {
    return [];
  }
};

// 웨딩홀 홈 > 슬롯 찜 추가
export const addLikeSlot = async (slotId) => {
  try {
    const res = await axios.post(`${BASE_URL}/v3/like/hall/${slotId}`);
    return res.status;
  } catch (err) {
    return null;
  }
};

// 웨딩홀 홈 > 슬롯 찜 삭제
export const removeLikeSlot = async (slotId) => {
  try {
    const res = await axios.delete(`${BASE_URL}/v3/like/hall/${slotId}`);
    return res.status;
  } catch (err) {
    return null;
  }
};

// 웨딩홀 홈 > 조건검색 > 웨딩홀 업체 검색 API
export const getSearchedPartner = (keyword: string) => {
  return axios.get<{ item: SearchedPartnerInfo[] }>(
    `${BASE_URL}/v3/book/${version}/partner/halls/keyword?keyword=${keyword}`,
  );
};

// 웨딩홀 홈 > 조건검색 > 웨딩홀 업체 > 최근 검색 업체 추가 API
export const addRecentKeywordToPartner = (partnerId: number) => {
  return axios.post(`${BASE_URL}/v3/book/halls/histories`, { partnerId });
};

// 웨딩홀 홈 > 조건검색 > 웨딩홀 업체 > 최근 검색 업체 조회 API
export const getRecentKeywordToPartner = () => {
  return axios.get<{
    item: {
      partnerId: number;
      partnerProfileName: string;
      partnerProfileUuid: string;
      weddingHallSearchHistoryId: number;
    }[];
  }>(`${BASE_URL}/v3/book/halls/histories`);
};

// 웨딩홀 홈 > 조건검색 > 웨딩홀 업체 > 최근 검색 업체 부분삭제 API
export const deleteRecentKeywordToPartner = (partnerId: number) => {
  return axios.delete(`${BASE_URL}/v3/book/halls/histories/${partnerId}`);
};

// 웨딩홀 홈 > 조건검색 > 웨딩홀 업체 > 최근 검색 업체 전체삭제 API
export const deleteAllRecentKeywordToPartner = () => {
  return axios.delete(`${BASE_URL}/v3/book/halls/histories`);
};

// 웨딩홀 홈 > 잔여타임 목록 조회API
export const getSlotList = async (params: any, slotFilter?: any) => {
  let validFilters;
  const validParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => {
      if (value === 0) return true;
      return !!value !== false;
    }),
  );

  if (!!slotFilter) {
    const filters = createPayload(slotFilter);
    validFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => {
        if (value === 0) return true;
        return !!value !== false;
      }),
    );
  }

  const params_qs = QueryString.stringify(validParams)
    ? "?" + QueryString.stringify(validParams)
    : "";
  const filters_qs = QueryString.stringify(validFilters)
    ? "&" + QueryString.stringify(validFilters)
    : "";

  try {
    const res = await axios.get<{ item: any; result: TResult }>(
      `${BASE_URL}/v3/wedding/day/remaining-wedding-days${params_qs}${filters_qs}`,
      // `${BASE_URL}/v3/book/wedding/day${params_qs}${filters_qs}`,
    );
    return res.data.item;
  } catch (err) {
    return {
      results: [],
    };
  }
};

//리뉴얼 된 즉시계약 페이지 잔여타임 목록 호출
export const getContractSlotList = async (params: any, slotFilter?: any) => {
  let validFilters;
  const validParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => {
      if (value === 0) return true;
      return !!value !== false;
    }),
  );

  if (!!slotFilter) {
    const filters = createPayload(slotFilter);
    validFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => {
        if (value === 0) return true;
        return !!value !== false;
      }),
    );
  }

  const params_qs = QueryString.stringify(validParams)
    ? "?" + QueryString.stringify(validParams)
    : "";
  const filters_qs = QueryString.stringify(validFilters)
    ? "&" + QueryString.stringify(validFilters)
    : "";

  try {
    const res = await axios.get<{ item: TContractSlotItem[]; total: number }>(
      `${BASE_URL}/v3/book/wedding/day${params_qs}${filters_qs}`,
    );
    return res.data;
  } catch (err) {
    return {
      results: [],
    };
  }
};
// 계약금 0원일때 결제시 호출할 함수
export const postZeroDepositPayment = (
  individualHallId: number,
  weddingDate: string,
  weddingTime: string,
  contractor: string,
  contractorTel: string,
  contractorEmail: string,
  contractorZipCode: string,
  contractorAddress: string,
  contractorDetailAddress: string,
  brideName: string,
  brideTel: string,
  groomName: string,
  groomTel: string,
  deposit: number,
  paymentAmount: number,
) => {
  const reqData = {
    ECH_INDIVIDUAL_HALL_ID: individualHallId,
    ECH_WEDDING_DATE: weddingDate,
    ECH_WEDDING_TIME: weddingTime,
    ECH_CONTRACTOR: contractor,
    ECH_CONTRACTOR_TEL: contractorTel,
    ECH_CONTRACTOR_EMAIL: contractorEmail,
    ECH_CONTRACTOR_ZIPCODE: contractorZipCode,
    ECH_CONTRACTOR_ADDRESS1: contractorAddress,
    ECH_CONTRACTOR_ADDRESS2: contractorDetailAddress,
    ECH_BRIDE_NAME: brideName,
    ECH_BRIDE_TEL: brideTel,
    ECH_GROOM_NAME: groomName,
    ECH_GROOM_TEL: groomTel,
    ECH_DEPOSIT: deposit,
    ECH_PAYMENT_AMOUNT: paymentAmount,
  };
  return axios.post(`${BASE_URL}/v3/book/${version}/halls/weddings/contracts`, reqData);
};

//웨딩홀 상세 정보탭 조회
export const getWeddingHallInfo = async (partnerUuid) => {
  try {
    const res = await axios.get<any>(
      `${BASE_URL}/v3/${version}/partner/hall/${partnerUuid}/detail`,
    );
    return res.data;
  } catch (err) {
    return {};
  }
};

// 상품탭, 잔여타임 필터(배열) -> 파라미터(객체) 형태로 변환
const createPayload = (tags) => {
  const payload = {
    minGuarantee: null,
    maxGuarantee: null,
    minRentalPrice: null,
    maxRentalPrice: null,
    minMealPrice: null,
    maxMealPrice: null,
    weddingTimes: [],
    dayOfWeeks: [],
    styles: [],
    mealTypes: [],
    ceremonyTypes: [],
    trafficTypes: [],
  };

  tags.forEach((tag) => {
    const key = tag.paramName;
    switch (tag.typeName) {
      case "GUARANTEE":
        // const guaranteeValues = tag.value.match(/\d+/g).map(Number);
        payload.minGuarantee = tag.value[0] <= HALL_FILTER_DEFAULT_MIN_GUARANTEE ? 0 : tag.value[0]; // 최소 인원 수로 설정 할 경우 0명 부터
        payload.maxGuarantee =
          tag.value[1] >= HALL_FILTER_DEFAULT_MAX_GUARANTEE ? 9999 : tag.value[1]; // 최대 인원 수로 설정 할 경우 초과 된 인원 수로 불러와야함.
        break;

      case "RENTAL_PRICE":
        const rentalFeeValues = tag.value.match(/\d+/g).map(Number);
        payload.minRentalPrice = rentalFeeValues[0];
        payload.maxRentalPrice = rentalFeeValues[1];
        break;

      case "MEAL_PRICE":
        //탐색과 웨딩홀 상품탭에서 식대 필터가 제외된 것 같아 일단 이렇게 수정.
        const mealFeeValues = tag.value; //.match(/\d+/g).map(Number);
        payload.minMealPrice = mealFeeValues[0];
        payload.maxMealPrice = mealFeeValues[1];
        break;

      case "WEDDING_TIME":
        if (tag.value === "20:00 ~") {
          payload.weddingTimes.push("20:00");
          payload.weddingTimes.push("21:00");
        } else {
          payload.weddingTimes.push(tag.value);
        }
        break;

      case "WEDDING_DAY":
      case "MEAL_TYPE":
      case "HALL_TYPE":
      case "WEDDING_METHOD":
      case "PARK_TRAFFIC":
        payload[key].push(tag.value);
        break;
    }
  });

  return payload;
};

// 웨딩홀 상세 > 상품탭 슬롯 조회
export const getWeddingHallSlots = async (
  partnerUuid,
  dates,
  months,
  tags,
  fetchSize,
  page,
  sortType,
) => {
  console.log(page);
  try {
    let params = {
      startDate: dates.startDate,
      endDate: dates.endDate,
      months: months,
      fetchSize: fetchSize,
      page: page,
      sortType: sortType,
      // dayOfWeeks:
    };

    if (!!tags) {
      const parsedParams = createPayload(tags);
      params = { ...parsedParams, ...params };
    }

    const validFilters = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => {
        if (value === 0) return true;
        return !!value !== false;
      }),
    ); // 값이 유효한 key만 필터링해서 쿼리스트링으로 넘김
    const qs = QueryString.stringify(validFilters) ? "?" + QueryString.stringify(validFilters) : "";
    const res = await axios.get<any>(`${BASE_URL}/v3/wedding/estimate/product/${partnerUuid}${qs}`);
    return res.data;
  } catch (err) {
    return {
      item: [],
      total: 0,
    };
  }
};

//웨딩홀 상세 사진탭 조회
export const getWeddingHallImgs = async (params, partnerUuid: string) => {
  try {
    if (params?.uuid) {
      const res = await axios.get<any>(
        `${BASE_URL}/v3/${version}/partner/${partnerUuid}/official-images?uuid=${params?.uuid}`,
      );

      const items = res.data.item;
      const representativeImgs = []; // 대표이미지배열
      const folderNames = new Set(); // 폴더명이 될 mainGroupKey 이름

      items.forEach((i) => {
        if (i.mainGroupKey === "representativeImageUrls") {
          //대표이미지인 경우에는 배열에 추가
          representativeImgs.push(i);
        } else if (!folderNames.has(i.mainGroupKey)) {
          // representativeImageUrls이 아닌 mainGroupKey 중 folderNames에 없는 경우
          representativeImgs.push(i);
          folderNames.add(i.mainGroupKey);
        }
      });

      // 앱웹과 데이터 형태 맞춤
      return {
        ...res,
        data: {
          ...res.data,
          item: representativeImgs,
          total: representativeImgs.length,
        },
      };
    }
  } catch (err) {
    console.error(err);
    return { data: { item: [] } };
  }
};

export const getWeddingHallFolderImgs = async ({
  params,
  partnerUuid,
  key = "amenities",
}: {
  params: any;
  partnerUuid: string;
  key?: string;
}) => {
  try {
    if (params?.uuid) {
      const res = await axios.get<any>(
        `${BASE_URL}/v3/${version}/partner/${partnerUuid}/official-images?uuid=${params?.uuid}`,
      );
      const items = res.data.item;
      const formatedImgs = [];

      items.forEach((i) => {
        if (i.mainGroupKey === params.key) {
          formatedImgs.push({ url: i.url, title: i.title });
        }
      });

      if (res.status === 200) {
        return {
          data: {
            ...res.data,
            item: formatedImgs,
            total: formatedImgs.length,
          },
        };
      }
    }
  } catch (err) {
    console.log("getWeddingHallFolderImgs 에러");
    return { data: { item: [] } };
  }
};

export const getWeddingHallReviewImgs = async (params, partnerUuid: string) => {
  const p = new URLSearchParams(params).toString();
  try {
    if (params?.uuid) {
      const res = await axios.get<any>(`${BASE_URL}/v1/partner/${partnerUuid}/image?${p}`);
      return res;
    }
  } catch (err) {
    console.error(err);
    return { data: { item: [] } };
  }
};

// 슬롯의 가격 상세 정보 조회
export const getProductPrice = async (weddingDayId: string) => {
  try {
    const res = await axios.get<{ item: TSlotPriceDetail }>(
      `${BASE_URL}/v3/wedding/estimate/product/detail/${weddingDayId}`,
    );
    return res.data.item;
  } catch (err) {
    return null;
  }
};

// 웨딩홀 슬롯 찜
export const putWeddingHallSlotLike = async (
  uuid: string,
  data: { partnerName: string; state: 0 | 1 },
) => {
  try {
    const res = await axios.put(`${BASE_URL}/v3/20240729/partner/${uuid}/like`, {
      state: data.state,
    });
    return res.data;
  } catch (err) {
    console.error(err);
    return {};
  }
};

type THallRegional = {
  uuid: string;
  coverUrl: string;
  type: number;
  name: string;
  address: string;
  mainRegion: string;
  subRegion: string;
  isLiked: boolean;
};

export const getWeddingHallRegional = async (uuids: string[]) => {
  try {
    const res = await axios.get<ServerResponse<THallRegional[]>>(
      `${BASE_URL}/v3/book/20240729/partner/halls/regional?uuids=${uuids.join(",")}`,
    );
    return res.data;
  } catch (err) {
    console.log(err);
    return err;
  }
};
